<template>
    <div class="d3-chart heat-map">
        <loader class="panel-loader" v-if="!isDataLoaded"></loader>
        <div class="chart-container" v-if="isDataLoaded && !hasError">
            <div class="chart-controls">
                <div class="scale-container">
                    <div class="scale">
                        <span v-bind:style="{ background: colors.start.color }">{{startLabel}}</span>
                        <span v-bind:style="{ width: '3em', background: 'linear-gradient(to right, ' + colors.start.color + ', ' + colors.mid.color + ')' }">&nbsp;</span>
                        <span v-bind:style="{ background: colors.mid.color }">{{midLabel}}</span>
                        <span v-bind:style="{ width: '3em', background: 'linear-gradient(to right, ' + colors.mid.color + ', ' + colors.end.color + ')' }">&nbsp;</span>
                        <span v-bind:style="{ background: colors.end.color }">{{endLabel}}</span>
                    </div>
                </div>
                <div class="filter" v-if="hasFilter">
                    <template v-for="(filterValue, filterName) in filter">
                        <select class="form-control" v-model="filter[filterName]" v-on:change="getData()">
                            <option v-for="filterOption in filterOptions[filterName]" v-bind:value="filterOption.value">{{filterOption.label}}</option>
                        </select>
                    </template>
                </div>
            </div>
            <div class="chart heat-map-container" ref="chartContainer">
                <div class="heat-map-legend" v-if="hasLegend">
                    <h4>{{legend['title']}}</h4>
                    <dl>
                        <template v-for="detail in legend['details']">
                            <dt>{{detail.label}}</dt>
                            <dd>{{detail.value}}</dd>
                        </template>
                    </dl>
                </div>
            </div>
        </div>
        <div class="error-container alert alert-danger" v-if="isDataLoaded && hasError">
            {{error}}
        </div>
        <div class="chart-generation-date" v-if="isDataLoaded && hasGenDate">
            Data generated at: {{content['genDate']}}
        </div>
    </div>
</template>

<script>
    import Vue from 'vue'
    import axios from 'axios'
    import querystring from 'querystring'
    import * as d3 from 'd3'
    import * as topojson from 'topojson'
    import Loader from '@/components/Loader'
    import ChartMixin from '@/mixins/ChartMixin.js'

    export default {
        props: ['contentSource', 'mapDataSource', 'id'],
        mixins: [ChartMixin],
        name: 'heatMap',
        data () {
            return {
                d3: d3,
                topojson: topojson,
                content: null,
                mapData: null,
                filter: null,
                filterOptions: [],
                components: {},
                legend: null,
                aspectRatio: 0.625,
                defaultColors: {
                    start: {
                        label: 'G 100%',
                        color: '#66ca38'
                    },
                    mid: {
                        label: '50/50',
                        color: '#09caf3'
                    },
                    end: {
                        label: '100% E',
                        color: '#023b6c'
                    }
                },
                colors: {}
            }
        },
        computed: {
            isDataLoaded () {
                return this.content !== null
            },
            hasGenDate () {
                return this.content.genDate !== undefined && this.content.genDate !== null
            },
            hasError () {
                return this.content.error !== undefined && this.content.error !== null
            },
            error () {
                return this.content.error || 'An unknown error has occurred'
            },
            hasFilter () {
                return this.filter !== null
            },
            hasLegend () {
                return this.legend !== null && this.legend !== undefined && typeof this.legend === 'object'
            },
            startLabel () { return this.colors.start.label },
            midLabel () { return this.colors.mid.label },
            endLabel () { return this.colors.end.label }
        },
        methods: {
            _scales () {
                this.components.scales = this.components.scales || {
                    color: d3.scaleLinear().domain([-1, 0, 1]).range(Object.values(this.colors).map(function (x) { return x.color })),
                    opacity: d3.scaleLinear().domain(
                        [
                            0,
                            this.d3.min(Object.values(this.content.data).map(function (x) { return x.total })),
                            this.d3.max(Object.values(this.content.data).map(function (x) { return x.total }))
                        ]
                    ).range([0, 0.3, 1])
                }
                return this.components.scales
            },
            _container () {
                this.components.container = this.components.container || this.d3.select(this.$refs.chartContainer)
                return this.components.container
            },
            _svg () {
                const geometry = this._geometry()
                this.components.svg = this.components.svg || this._container().append('svg')
                        .attrs({
                            width: geometry.containerWidth,
                            height: geometry.containerHeight,
                            fill: 'none',
                            'pointer-events': 'all'
                        })
                        .on('click', this.stopped(this), true)

                return this.components.svg
            },
            _geometry () {
                this.components.geometry = this.components.geometry || {
                    containerHeight: 0,
                    containerWidth: 0,
                    height: 0,
                    width: 0
                }

                return this.components.geometry
            },
            _zoom () {
                this.components.zoom = this.components.zoom || this.d3.zoom()
                    .scaleExtent([1, 8])
                    .on('zoom', this.zoomed(this))

                return this.components.zoom
            },
            _map () {
                this.components.map = this.components.map || this._svg().append('g')

                return this.components.map
            },
            _active () {
                this.components.active = this.components.active || this.d3.select(null)

                return this.components.active
            },
            _geoPath (renew) {
                let geoPath

                if (renew) {
                    geoPath = this.newGeoPath()
                } else {
                    geoPath = this.components.geoPath || this.newGeoPath()
                }

                this.components.geoPath = geoPath

                return geoPath
            },
            setActive (active) {
                this.components.active = active

                return this.components.active
            },
            newGeoPath () {
                const geometry = this._geometry()
                const projection = d3.geoAlbersUsa()
                    .scale(geometry.height * 2)
                    .translate([geometry.containerWidth / 2, geometry.containerHeight / 2])

                return d3.geoPath().projection(projection)
            },
            initializeZoom () {
                this._svg().call(this._zoom())
            },
            calculateGeometry () {
                const geometry = this._geometry()
                const container = this._container()

                if (container.node() === null) {
                    return
                }

                geometry.containerWidth = container.node().offsetWidth
                geometry.containerHeight = container.node().offsetHeight

                if (geometry.containerWidth * this.aspectRatio > geometry.containerHeight) {
                    geometry.height = geometry.containerHeight
                    geometry.width = Math.floor(geometry.containerHeight * (1 / this.aspectRatio))
                } else {
                    geometry.width = geometry.containerWidth
                    geometry.height = Math.floor(geometry.containerWidth * this.aspectRatio)
                }
            },
            appendBackground () {
                const geometry = this._geometry()

                this._svg().append('rect')
                    .attr('class', 'background')
                    .attr('width', geometry.width)
                    .attr('height', geometry.height)
                    .on('click', this.reset(this))
            },
            drawStateFills () {
                const self = this
                this._map().selectAll('path')
                    .data(this.topojson.feature(this.mapData, this.mapData.objects.states).features)
                    .enter().append('path')
                    .attrs({
                        'd': self._geoPath(),
                        'class': 'feature',
                        'fill': function (d) {
                            return self.content.data[d.properties.code] !== undefined
                                ? self._scales().color(self.content.data[d.properties.code].balance)
                                : self._scales().color(0)
                        },
                        'opacity': function (d) {
                            return self.content.data[d.properties.code] !== undefined
                                ? self._scales().opacity(self.content.data[d.properties.code].total)
                                : 0
                        }
                    })
                    .on('click', this.clicked(self))
            },
            drawStateOutlines () {
                this._map().append('path')
                    .datum(this.topojson.mesh(this.mapData, this.mapData.objects.states))
                    .attrs({
                        class: 'mesh',
                        'pointer-events': 'none',
                        d: this._geoPath()
                    })
            },
            buildLegend (state) {
                const output = []

                if (this.content.data[state] !== undefined) {
                    for (let legend of this.content.legendFormat) {
                        output.push({label: legend.label, value: this.formatValue(this.content.data[state], legend.values)})
                    }
                }

                return output
            },
            drawMap () {
                this.legend = null
                this._svg().remove()
                this.components.scales = null
                this.components = {}
                this.calculateGeometry()
                this._geoPath(true)
                this.initializeZoom()
                this.appendBackground()
                this.drawStateFills()
                this.drawStateOutlines()
            },
            stopped (self) {
                return function () {
                    if (self.d3.event.defaultPrevented) {
                        self.d3.event.stopPropagation()
                    }
                }
            },
            zoomed (self) {
                return function () {
                    const map = self._map()
                    map.style('stroke-width', 1.5 / self.d3.event.scale + 'px')
                    map.attr('transform', self.d3.event.transform)
                }
            },
            reset (self) {
                return function () {
                    let active = self._active()
                    active.classed('active', false)
                    active = self.setActive(self.d3.select(null))

                    self._svg().transition()
                        .duration(750)
                        .call(self._zoom().transform, self.d3.zoomIdentity)
                    self.legend = null
                }
            },
            clicked (self) {
                return function (d) {
                    let active = self._active()
                    const geometry = self._geometry()
                    if (active.node() === this) return self.reset(self)()
                    active.classed('active', false)
                    active = self.setActive(self.d3.select(this).classed('active', true))

                    let bounds = self._geoPath().bounds(d)
                    let dx = bounds[1][0] - bounds[0][0]
                    let dy = bounds[1][1] - bounds[0][1]
                    let x = (bounds[0][0] + bounds[1][0]) / 2
                    let y = (bounds[0][1] + bounds[1][1]) / 2
                    let scale = Math.max(1, Math.min(8, 0.9 / Math.max(dx / geometry.width, dy / geometry.height)))
                    let translate = [geometry.containerWidth / 2 - scale * x, geometry.containerHeight / 2 - scale * y]

                    self.legend = {
                        title: d.properties.name,
                        details: self.buildLegend(d.properties.code)
                    }

                    self._svg().transition()
                        .duration(500)
                        .call(self._zoom().transform, self.d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale))
                }
            },
            getData () {
                this.content = null
                let self = this
                return axios.post(this.contentSource, querystring.stringify(Object.assign({id: this.id}, this.filter)), {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(function (response) {
                        if (response.data.result) {
                            self.setData(response.data.content)
                        } else {
                            self.setData({error: response.data.message || 'An unknown error has occurred'})
                        }
                    })
                    .catch((error) => {
                        if (error instanceof Error) {
                            self.setData({error: error.message || 'An unknown error has occurred'})
                        }
                    })
            },
            setData (content) {
                const self = this

                let setData = function () {
                    self.content = content
                    self.colors = Vue.util.extend(Vue.util.extend({}, self.defaultColors), content.colors || {})
                    self.filter = self.content.filter || null
                    self.filterOptions = self.content.filterOptions || []

                    self.$nextTick().then(() => {
                        self.drawMap()
                    })
                }

                if (self.mapData === null) {
                    this.d3.json(this.mapDataSource, function (error, us) {
                        if (error) throw error

                        self.mapData = us
                        setData()
                    })
                } else {
                    setData()
                }
            }
        },
        created () {
            if (this.contentSource !== undefined && this.contentSource !== null) {
                this.getData()
            }

            this.forceResize = new CustomEvent('resize')

            let map = this
            window.addEventListener('resize', function () {
                map.drawMap()
            })
        },
        components: { Loader }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>

</style>



// WEBPACK FOOTER //
// HeatMap.vue?3e3a6f0a